import React, { useRef, useEffect, useCallback, useState, useMemo } from "react";
import { useItems } from "../../contexts/item_context";
import "./editor.css";

/* ─── HTML escape ────────────────────────────────────────── */
const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/* ─── Syntax highlight ───────────────────────────────────── */
const highlight = (code) => {
    if (!code) return "";
    return esc(code).replace(
        /("(\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][-+]?\d+)?)/g,
        (m) => {
            let c = "j-num";
            if (/^"/.test(m)) c = /:$/.test(m) ? "j-key" : "j-str";
            else if (/true|false/.test(m)) c = "j-bool";
            else if (/null/.test(m)) c = "j-null";
            return `<span class="${c}">${m}</span>`;
        }
    );
};


const injectMarks = (html, term, activeIdx) => {
    if (!term) return { html, total: 0 };
    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    const walker = document.createTreeWalker(tmp, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let n;
    while ((n = walker.nextNode())) nodes.push(n);

    const lower = term.toLowerCase();
    let total = 0;

    for (const node of nodes) {
        const txt = node.textContent;
        const lo = txt.toLowerCase();
        let last = 0, found = false;
        const frag = document.createDocumentFragment();

        let idx;
        while ((idx = lo.indexOf(lower, last)) !== -1) {
            found = true;
            if (idx > last) frag.appendChild(document.createTextNode(txt.slice(last, idx)));
            const mk = document.createElement("mark");
            mk.className = total === activeIdx ? "e-mark e-mark--cur" : "e-mark";
            mk.textContent = txt.slice(idx, idx + lower.length);
            frag.appendChild(mk);
            total++;
            last = idx + lower.length;
        }
        if (found) {
            if (last < txt.length) frag.appendChild(document.createTextNode(txt.slice(last)));
            node.parentNode.replaceChild(frag, node);
        }
    }
    return { html: tmp.innerHTML, total };
};

/* ─── Count plain-text matches ───────────────────────────── */
const countMatches = (text, term) => {
    if (!term) return 0;
    let n = 0, i = 0;
    const lo = text.toLowerCase(), tlo = term.toLowerCase();
    while ((i = lo.indexOf(tlo, i)) !== -1) { n++; i++; }
    return n;
};

/* ─── Bracket-pair scanner ───────────────────────────────── */
const scanBrackets = (text) => {
    const pairs = [], stack = [], starts = [0];
    let inStr = false, esc2 = false, line = 0;

    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (c === "\n") { line++; starts.push(i + 1); continue; }
        if (esc2) { esc2 = false; continue; }
        if (c === "\\" && inStr) { esc2 = true; continue; }
        if (c === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (c === "{" || c === "[") {
            stack.push({ ch: c, line, pos: i, lineStart: starts[line] });
        } else if ((c === "}" || c === "]") && stack.length) {
            const o = stack[stack.length - 1];
            if ((c === "}" && o.ch === "{") || (c === "]" && o.ch === "[")) {
                stack.pop();
                if (o.line !== line)
                    pairs.push({ openLine: o.line, closeLine: line, openPos: o.pos, openLineStart: o.lineStart });
            }
        }
    }
    return pairs;
};

/* ─── Compute display text (folds applied) ───────────────── */
const foldText = (raw, folded, fMap) => {
    if (!folded.size) return { dt: raw, toRaw: raw.split("\n").map((_, i) => i) };
    const rows = raw.split("\n"), out = [], map = [];
    let i = 0;
    while (i < rows.length) {
        const p = fMap.get(i);
        if (folded.has(i) && p) {
            const line = rows[i];
            const oc = raw[p.openPos];
            const cc = oc === "{" ? "}" : "]";
            const col = p.openPos - p.openLineStart;
            out.push(`${line.slice(0, col + 1)} ⋯ ${cc}`);
            map.push(i);
            i = p.closeLine + 1;
        } else {
            out.push(rows[i]);
            map.push(i);
            i++;
        }
    }
    return { dt: out.join("\n"), toRaw: map };
};

/* ─── Cursor helpers for contenteditable ─────────────────── */
const saveCursor = (el) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const r = sel.getRangeAt(0).cloneRange();
    r.selectNodeContents(el);
    r.setEnd(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset);
    return r.toString().length;
};

const restoreCursor = (el, off) => {
    try {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        let rem = off, nd;
        while ((nd = walker.nextNode())) {
            if (rem <= nd.length) {
                const r = document.createRange();
                r.setStart(nd, rem); r.collapse(true);
                const sel = window.getSelection();
                sel.removeAllRanges(); sel.addRange(r);
                return;
            }
            rem -= nd.length;
        }
    } catch { }
};

/* ═══════════════════════════════════════════════════════════ */
export default function Editor() {
    const { activeItem, updateItemData, beautifyJSON } = useItems();
    const areaRef = useRef(null);
    const lineRef = useRef(null);
    const lastHtml = useRef("");

    /* ── fold state ── */
    const [folded, setFolded] = useState(new Set());
    /* ── search state ── */
    const [srOpen, setSrOpen] = useState(false);
    const [srTerm, setSrTerm] = useState("");
    const [srIdx, setSrIdx] = useState(0);
    const srInputRef = useRef(null);

    const raw = typeof activeItem?.text === "string" ? activeItem.text : "";

    /* clear per-file state on file switch */
    useEffect(() => {
        setFolded(new Set());
        setSrTerm("");
        setSrOpen(false);
        setSrIdx(0);
    }, [activeItem?.id]);

    /* bracket pairs */
    const pairs = useMemo(() => scanBrackets(raw), [raw]);
    const fMap = useMemo(() => {
        const m = new Map();
        pairs.forEach((p) => m.set(p.openLine, p));
        return m;
    }, [pairs]);

    /* display text */
    const { dt, toRaw } = useMemo(() => foldText(raw, folded, fMap), [raw, folded, fMap]);
    const dLines = dt.split("\n");
    const anyFold = folded.size > 0;

    /* match count (for "N of M" display) */
    const total = useMemo(() => countMatches(dt, srTerm), [dt, srTerm]);

    /* ── DOM update ── */
    useEffect(() => {
        const el = areaRef.current;
        if (!el) return;

        let html = highlight(dt);
        if (srTerm) {
            const res = injectMarks(html, srTerm, srIdx);
            html = res.html;
        }

        if (lastHtml.current === html) return;

        // Save cursor position before updating DOM
        const curPos = document.activeElement === el ? saveCursor(el) : null;
        lastHtml.current = html;
        el.innerHTML = html;

        // Restore cursor position after DOM update
        if (curPos !== null) restoreCursor(el, curPos);

        /* scroll active match into view */
        if (srTerm) {
            const mk = el.querySelector(".e-mark--cur");
            if (mk) mk.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    }, [dt, srTerm, srIdx]);

    /* ── sync line-num scroll ── */
    const onScroll = useCallback(() => {
        if (lineRef.current && areaRef.current)
            lineRef.current.scrollTop = areaRef.current.scrollTop;
    }, []);

    /* ── input ── */
    const onInput = useCallback(() => {
        const el = areaRef.current;
        if (!el) return;
        if (anyFold) { el.innerHTML = lastHtml.current; return; }
        updateItemData(el.innerText.replace(/\r\n/g, "\n").replace(/\r/g, "\n").replace(/\n$/, ""));
    }, [anyFold, updateItemData]);

    /* ── paste (plain text only) ── */
    const onPaste = useCallback((e) => {
        e.preventDefault();
        document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    }, []);

    /* ── prevent edits while folded ── */
    const onBeforeInput = useCallback((e) => {
        if (anyFold) e.preventDefault();
    }, [anyFold]);

    /* ── keydown ── */
    const onKey = useCallback((e) => {
        const mod = e.ctrlKey || e.metaKey;
        /* Ctrl+F → open search */
        if (mod && (e.key === "f" || e.key === "F")) {
            e.preventDefault();
            setSrOpen(true);
            setTimeout(() => srInputRef.current?.focus(), 30);
            return;
        }
        /* Escape → close search */
        if (e.key === "Escape" && srOpen) {
            setSrOpen(false); setSrTerm(""); setSrIdx(0);
            areaRef.current?.focus();
            return;
        }
        /* Tab → 4 spaces */
        if (e.key === "Tab") {
            e.preventDefault();
            if (!anyFold) document.execCommand("insertText", false, "    ");
            return;
        }
        /* Ctrl+B → beautify */
        if (mod && (e.key === "b" || e.key === "B")) {
            e.preventDefault(); beautifyJSON();
        }
    }, [srOpen, anyFold, beautifyJSON]);

    /* ── fold toggle ── */
    const toggleFold = useCallback((ri) => {
        setFolded((p) => { const n = new Set(p); n.has(ri) ? n.delete(ri) : n.add(ri); return n; });
    }, []);

    /* ── search navigation ── */
    const next = useCallback(() => setSrIdx((i) => total ? (i + 1) % total : 0), [total]);
    const prev = useCallback(() => setSrIdx((i) => total ? (i - 1 + total) % total : 0), [total]);
    useEffect(() => setSrIdx(0), [srTerm]);

    const onSrKey = (e) => {
        if (e.key === "Enter") { e.shiftKey ? prev() : next(); }
        if (e.key === "Escape") { setSrOpen(false); setSrTerm(""); setSrIdx(0); areaRef.current?.focus(); }
    };

    if (!activeItem) return <div className="ed-empty">No file selected</div>;

    return (
        <div className="ed-root">
            {/* ── Search Bar ── */}
            {srOpen && (
                <div className="ed-searchbar">
                    <div className="sr-wrap">
                        <svg className="sr-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            ref={srInputRef}
                            className="sr-input"
                            type="text"
                            placeholder="Find…"
                            value={srTerm}
                            onChange={(e) => setSrTerm(e.target.value)}
                            onKeyDown={onSrKey}
                            spellCheck={false}
                        />
                        {srTerm && (
                            <span className="sr-count">
                                {total === 0 ? "No results" : `${srIdx + 1} / ${total}`}
                            </span>
                        )}
                    </div>
                    <button className="sr-nav" onClick={prev} title="Previous (Shift+Enter)">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>
                    </button>
                    <button className="sr-nav" onClick={next} title="Next (Enter)">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </button>
                    <button className="sr-close" onClick={() => { setSrOpen(false); setSrTerm(""); setSrIdx(0); }} title="Close (Esc)">×</button>
                </div>
            )}
            {/* ── Editor Body ── */}
            <div className="ed-body">
                {/* Gutter: line numbers + fold arrows */}
                <div ref={lineRef} className="ed-gutter" aria-hidden="true">
                    {dLines.map((_, vi) => {
                        const ri = toRaw[vi];
                        const canFold = fMap.has(ri);
                        const isFolded = folded.has(ri);
                        return (
                            <div key={vi} className="gutter-row">
                                <span
                                    className={`fold-arrow${canFold ? " fold-arrow--on" : ""}`}
                                    onClick={() => canFold && toggleFold(ri)}
                                    title={canFold ? (isFolded ? "Expand" : "Fold") : undefined}
                                >
                                    {canFold && (isFolded
                                        ? <svg width="7" height="7" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5l8 7-8 7z" /></svg>
                                        : <svg width="7" height="7" viewBox="0 0 24 24" fill="currentColor"><path d="M5 8l7 8 7-8z" /></svg>
                                    )}
                                </span>
                                <span className="gutter-num">{vi + 1}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Contenteditable code area — single element, Ctrl+F finds text once */}
                <div
                    ref={areaRef}
                    className="ed-code"
                    contentEditable="true"
                    suppressContentEditableWarning
                    onInput={onInput}
                    onKeyDown={onKey}
                    onPaste={onPaste}
                    onBeforeInput={onBeforeInput}
                    onScroll={onScroll}
                    spellCheck={false}
                    data-ph="Paste or type JSON here…   Ctrl+B = Beautify"
                />
            </div>
        </div>
    );
}
